import { userIdentitySchema, apiKeyGenerationSchema, apiKeyStatusSchema, authEventResponseSchema, } from '../contracts/account';
import { parseSchema } from '../errors';
import { slackSettingsResponseSchema, upsertSlackSettingsBodySchema, upsertSlackSettingsResponseSchema, } from '../contracts/slack-settings';
import { yaraExclusionsListSchema, addYaraExclusionBodySchema, addYaraExclusionResponseSchema, } from '../contracts/yara-exclusions';
import { extensionIdSchema } from '../contracts/extensions';
import { userPreferenceSchema, userPreferencesResponseSchema, updateUserPreferenceBodySchema, } from '../contracts/user-preferences';
/**
 * Get current user information
 */
export async function getCurrentUser(client) {
    const response = await client.get('/account/whoami');
    return parseSchema(userIdentitySchema, response);
}
/**
 * Generate a new personal API key
 */
export async function generatePersonalApiKey(client) {
    const response = await client.post('/account/keys/generate');
    return parseSchema(apiKeyGenerationSchema, response);
}
/**
 * Get API key status for current user
 */
export async function getApiKeyStatus(client) {
    const response = await client.get('/account/keys/status');
    return parseSchema(apiKeyStatusSchema, response);
}
/**
 * Get user's personal Slack settings.
 * Returns null when no Slack settings are configured.
 */
export async function getUserSlackSettings(client) {
    const response = await client.get('/account/settings/slack');
    if (response === null || response === undefined) {
        return null;
    }
    return parseSchema(slackSettingsResponseSchema, response);
}
/**
 * Upsert user's personal Slack settings
 */
export async function upsertUserSlackSettings(client, data) {
    const validatedData = parseSchema(upsertSlackSettingsBodySchema, data);
    const response = await client.put('/account/settings/slack', validatedData);
    return parseSchema(upsertSlackSettingsResponseSchema, response);
}
/**
 * Delete user's personal Slack settings
 */
export async function deleteUserSlackSettings(client) {
    await client.delete('/account/settings/slack');
}
/**
 * Get user's personal YARA excluded extensions
 */
export async function getUserYaraExclusions(client) {
    const response = await client.get('/yara/exclusions');
    return parseSchema(yaraExclusionsListSchema, response);
}
/**
 * Add extension to user's personal YARA exclusion list
 */
export async function addUserYaraExclusion(client, data) {
    const validatedData = parseSchema(addYaraExclusionBodySchema, data);
    const response = await client.post('/yara/exclusions', validatedData);
    return parseSchema(addYaraExclusionResponseSchema, response);
}
/**
 * Remove extension from user's personal YARA exclusion list
 */
export async function removeUserYaraExclusion(client, extensionId) {
    const validatedId = parseSchema(extensionIdSchema, extensionId);
    await client.delete(`/yara/exclusions/${encodeURIComponent(validatedId)}`);
}
/**
 * Log a login event for the current user
 */
export async function logAccountLogin(client) {
    const response = await client.post('/account/login');
    return parseSchema(authEventResponseSchema, response);
}
/**
 * Log a logout event for the current user
 */
export async function logAccountLogout(client) {
    const response = await client.post('/account/logout');
    return parseSchema(authEventResponseSchema, response);
}
/**
 * Get the authenticated user's preferences.
 *
 * The backend's GET handler merges any persisted rows with
 * `DEFAULT_USER_PREFERENCES`, so the response always contains exactly one
 * entry per `UserPreferenceKey`. Callers can rely on every key being present.
 */
export async function getUserPreferences(client) {
    const response = await client.get('/account/preferences');
    return parseSchema(userPreferencesResponseSchema, response);
}
/**
 * Upsert a single user preference.
 *
 * The route writes exactly one row per request — to update multiple
 * preferences, call this function once per key.
 */
export async function updateUserPreference(client, body) {
    const validatedBody = parseSchema(updateUserPreferenceBodySchema, body);
    const response = await client.put('/account/preferences', validatedBody);
    return parseSchema(userPreferenceSchema, response);
}
//# sourceMappingURL=account.js.map